import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { getFeaturedComics, getLatestUpdates, getComicsByCategory } from '@/mocks/comics';
import { SITE_NAME, SITE_DESCRIPTION, CATEGORIES } from '@/mocks/site';

export default function Home() {
  const featuredComics = getFeaturedComics();
  const latestUpdates = getLatestUpdates(8);
  const hotRomance = getComicsByCategory('romance').slice(0, 6);
  const hotAction = getComicsByCategory('action').slice(0, 6);
  const recentComics = getComicsByCategory('all').sort(() => Math.random() - 0.5).slice(0, 6);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: 'https://hanhancomic.com/',
    description: SITE_DESCRIPTION,
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://hanhancomic.com/category/all?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: 'https://hanhancomic.com/',
    description: SITE_DESCRIPTION,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[480px] md:h-[580px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20with%20vibrant%20colors%2C%20dynamic%20action%20scene%2C%20multiple%20characters%20in%20comic%20style%2C%20warm%20red%20and%20gold%20tones%2C%20dramatic%20lighting%2C%20epic%20composition%2C%20clean%20artwork%20with%20smooth%20gradients%2C%20no%20text&width=1600&height=900&seq=hero-bg-hanhan&orientation=landscape"
              alt="汗汗漫画 - 最全的韩国漫画在线阅读平台"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-4 font-heading leading-tight">
                最全的下拉式韩漫
              </h1>
              <p className="text-base md:text-lg text-background-50/90 max-w-2xl mb-8 leading-relaxed">
                汗汗漫画提供海量韩国漫画资源，恋爱甜宠、奇幻冒险、热血动作应有尽有，<br className="hidden md:block" />
                免费在线观看，下拉式流畅阅读体验，随时随地畅享韩漫世界
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Link
                  to="/category/hot"
                  className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 hover:scale-105 shadow-lg shadow-primary-500/30 whitespace-nowrap"
                >
                  开始阅读
                </Link>
                <Link
                  to="/category/all"
                  className="px-8 py-3 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 font-semibold text-sm border border-background-50/30 hover:bg-background-50/30 transition-all duration-300 whitespace-nowrap"
                >
                  浏览分类
                </Link>
              </div>
            </div>
          </section>

          {/* Stats Bar */}
          <section className="bg-primary-500 py-4">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '20,000+', label: '漫画资源' },
                  { value: '120万+', label: '注册用户' },
                  { value: '8,000+', label: '每日更新' },
                  { value: '99.9%', label: '高清画质' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p className="text-2xl md:text-3xl font-bold text-background-50">{stat.value}</p>
                    <p className="text-xs md:text-sm text-background-50/80 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Section */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">万人追更的必看神作，每一部都不容错过</p>
              </div>
              <Link
                to="/category/hot"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} showCategory />
              ))}
            </div>
          </section>

          {/* Category Navigation */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">漫画分类</h2>
                <p className="text-sm text-foreground-500 mt-2">多种分类，总有一款适合你</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3">
                {CATEGORIES.filter((c) => c.key !== 'all').map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.key}`}
                    className="flex flex-col items-center justify-center gap-2 p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                      <i className="ri-book-read-line text-xl"></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                      {cat.label}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">最新更新</h2>
                <p className="text-sm text-foreground-500 mt-1">刚刚更新的热门漫画，第一时间追更</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {latestUpdates.map((comic) => (
                <ComicCard key={comic.id} comic={comic} showCategory />
              ))}
            </div>
          </section>

          {/* Romance & Action Parallel */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Romance */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-foreground-900 font-heading">恋爱甜宠</h2>
                      <p className="text-xs text-foreground-500 mt-1">甜蜜浪漫的恋爱故事</p>
                    </div>
                    <Link
                      to="/category/romance"
                      className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
                    >
                      更多 <i className="ri-arrow-right-s-line"></i>
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                    {hotRomance.map((comic) => (
                      <ComicCard key={comic.id} comic={comic} />
                    ))}
                  </div>
                </div>

                {/* Action */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-foreground-900 font-heading">热血动作</h2>
                      <p className="text-xs text-foreground-500 mt-1">燃到爆的打斗场面</p>
                    </div>
                    <Link
                      to="/category/action"
                      className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap transition-colors"
                    >
                      更多 <i className="ri-arrow-right-s-line"></i>
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                    {hotAction.map((comic) => (
                      <ComicCard key={comic.id} comic={comic} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-16 md:py-20 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20colorful%20abstract%20composition%20with%20soft%20gradients%2C%20warm%20red%20and%20amber%20tones%2C%20smooth%20flowing%20shapes%2C%20dreamy%20atmosphere%2C%20no%20text%2C%20elegant%20modern%20design&width=1600&height=600&seq=cta-hanhan&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50"></div>
            <div className="relative max-w-3xl mx-auto text-center px-4">
              <h2 className="text-2xl md:text-4xl font-bold text-background-50 mb-4 font-heading">
                立即开启你的韩漫之旅
              </h2>
              <p className="text-sm md:text-base text-background-50/80 mb-8 max-w-xl mx-auto">
                加入汗汗漫画，与百万读者一起畅享最全下拉式韩漫体验。免费、高清、更新快！
              </p>
              <Link
                to="/category/hot"
                className="inline-flex px-8 py-3 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-all duration-300 hover:scale-105 shadow-lg shadow-accent-500/30 whitespace-nowrap"
              >
                立即免费阅读
              </Link>
            </div>
          </section>

          {/* You May Also Like */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">猜你喜欢</h2>
              <p className="text-sm text-foreground-500 mt-1">根据你的阅读偏好为你推荐</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {recentComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} showCategory />
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-10">
                常见问题
              </h2>
              <div className="space-y-4">
                {[
                  { q: '汗汗漫画是免费的吗？', a: '是的！汗汗漫画上的所有韩国漫画完全免费，无需注册即可在线观看，真正做到无门槛阅读。' },
                  { q: '什么是下拉式韩漫？', a: '下拉式韩漫是适合手机竖屏阅读的漫画形式，画面从上到下连续排列，只需向下滑动即可流畅阅读，无需翻页。' },
                  { q: '漫画多久更新一次？', a: '我们每天都会同步更新连载中的热门韩漫，大部分作品每周更新1-2话，确保你第一时间看到最新内容。' },
                  { q: '支持离线下载吗？', a: '目前我们主打在线流畅阅读体验，暂不支持离线下载功能，但加载速度快，即使网络一般也能流畅阅读。' },
                  { q: '遇到问题如何联系？', a: '可以通过关于我们页面的联系方式、邮箱或在线客服与我们取得联系，我们会尽快为你解决。' },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/50 transition-colors">
                      <span className="text-sm font-medium text-foreground-800">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
              <script type="application/ld+json">
                {JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'FAQPage',
                  mainEntity: [
                    { q: '汗汗漫画是免费的吗？', a: '是的！汗汗漫画上的所有韩国漫画完全免费，无需注册即可在线观看。' },
                    { q: '什么是下拉式韩漫？', a: '下拉式韩漫是适合手机竖屏阅读的漫画形式，画面从上到下连续排列，只需向下滑动即可流畅阅读，无需翻页。' },
                    { q: '漫画多久更新一次？', a: '我们每天都会同步更新连载中的热门韩漫，大部分作品每周更新1-2话。' },
                    { q: '支持离线下载吗？', a: '目前我们主打在线流畅阅读体验，暂不支持离线下载功能。' },
                    { q: '遇到问题如何联系？', a: '可以通过关于我们页面的联系方式、邮箱或在线客服与我们取得联系。' },
                  ].map((e, idx) => ({
                    '@type': 'Question',
                    name: e.q,
                    acceptedAnswer: { '@type': 'Answer', text: e.a },
                  })),
                })}
              </script>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}